import * as React from "react";
import { useLocation } from "wouter";
import { Bell, Check, Trash2, AlertTriangle, Package, ChevronRight } from "lucide-react";
import { apiFetch } from "@/lib/api.js";
import { useToast } from "@/components/Toast.js";
import { cn } from "@/lib/utils.js";

interface Notification {
  id: string; type: string; title: string; message: string; itemId: string | null; goalId: string | null; actionUrl: string | null; read: boolean; createdAt: string;
}
interface LowStockItem {
  id: string; name: string; quantity: number; lowStockThreshold: number; spaceName: string | null;
}

export default function NotificationsPage() {
  const [notifications, setNotifications] = React.useState<Notification[]>([]);
  const [lowStockItems, setLowStockItems] = React.useState<LowStockItem[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [, navigate] = useLocation();
  const toast = useToast();

  const load = React.useCallback(async () => {
    try {
      const [n, ls] = await Promise.all([
        apiFetch<Notification[]>("/api/notifications"),
        apiFetch<LowStockItem[]>("/api/items/low-stock"),
      ]);
      setNotifications(n);
      setLowStockItems(ls);
    } catch {} finally { setLoading(false); }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const markRead = async (id: string) => {
    try {
      await apiFetch(`/api/notifications/${id}/read`, { method: "PUT" });
      setNotifications((prev) => prev.map((n) => n.id === id ? { ...n, read: true } : n));
    } catch (e: any) { toast.show(e.message, "error"); }
  };

  const markAllRead = async () => {
    try {
      await apiFetch("/api/notifications/read-all", { method: "PUT" });
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      toast.show("All marked as read");
    } catch (e: any) { toast.show(e.message, "error"); }
  };

  const deleteNotification = async (id: string) => {
    try {
      await apiFetch(`/api/notifications/${id}`, { method: "DELETE" });
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    } catch (e: any) { toast.show(e.message, "error"); }
  };

  const unreadCount = notifications.filter((n) => !n.read).length;

  if (loading) return <div className="space-y-4">{[1,2,3].map((i) => <div key={i} className="h-20 bg-clay-muted rounded-lg animate-pulse" />)}</div>;

  return (
    <div className="space-y-8">
      <header>
        <div className="flex items-center gap-2 mb-1"><Bell className="w-5 h-5 text-primary" /><span className="text-sm font-medium text-primary">Notifications</span></div>
        <h1 className="text-2xl font-semibold text-ink font-headline tracking-[-0.01em]">Notifications</h1>
      </header>

      {/* Low Stock Alerts */}
      {lowStockItems.length > 0 && (
        <section className="bg-white rounded-lg border border-clay-border p-6">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-5 h-5 text-warning" />
            <h2 className="text-[1.0625rem] font-semibold text-ink">Low Stock ({lowStockItems.length})</h2>
          </div>
          <div className="space-y-2">
            {lowStockItems.map((item) => (
              <button key={item.id} onClick={() => navigate(`/inventory?highlight=${item.id}`)}
                className="w-full flex items-center gap-3 p-3 rounded-md hover:bg-paper text-left transition-colors">
                <div className="w-10 h-10 rounded-md bg-warning-wash flex items-center justify-center shrink-0">
                  <Package className="w-5 h-5 text-warning" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-ink">{item.name}</p>
                  <p className="text-[0.8125rem] text-faint">{item.spaceName ?? "No location"} · <span className="tabular-nums">{item.quantity}</span> left (min: <span className="tabular-nums">{item.lowStockThreshold}</span>)</p>
                </div>
                <ChevronRight className="w-4 h-4 text-faint shrink-0" />
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Notifications */}
      <section className="bg-white rounded-lg border border-clay-border p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-[1.0625rem] font-semibold text-ink">
            All Notifications {unreadCount > 0 && <span className="text-sm font-normal text-faint">({unreadCount} unread)</span>}
          </h2>
          {unreadCount > 0 && (
            <button onClick={markAllRead} className="text-[0.8125rem] text-primary hover:text-amethyst-ink font-medium">Mark all read</button>
          )}
        </div>

        {notifications.length === 0 ? (
          <div className="bg-sage-wash rounded-lg border border-clay-border text-center py-10 px-6">
            <Bell className="w-10 h-10 text-sage-deep mx-auto mb-3" />
            <p className="text-sm text-body">You're all caught up — no notifications yet.</p>
          </div>
        ) : (
          <div className="space-y-1">
            {notifications.map((n) => (
              <div
                key={n.id}
                className={cn("flex items-start gap-3 p-3 rounded-md transition-colors", !n.read && "bg-amethyst-soft", n.actionUrl && "cursor-pointer hover:bg-paper")}
                onClick={n.actionUrl ? () => { markRead(n.id); navigate(n.actionUrl!); } : undefined}
              >
                <div className={cn("w-2 h-2 rounded-pill mt-2 shrink-0", n.read ? "bg-transparent" : "bg-primary")} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-ink">{n.title}</p>
                  <p className="text-[0.8125rem] text-body mt-0.5">{n.message}</p>
                  <p className="text-[0.8125rem] text-faint mt-1 tabular-nums">{new Date(n.createdAt).toLocaleDateString()}</p>
                </div>
                <div className="flex gap-1 shrink-0">
                  {!n.read && (
                    <button onClick={() => markRead(n.id)} className="p-1.5 rounded-md hover:bg-clay-muted transition-colors" title="Mark read" aria-label={`Mark "${n.title}" as read`}>
                      <Check className="w-3.5 h-3.5 text-faint" />
                    </button>
                  )}
                  <button onClick={() => deleteNotification(n.id)} className="p-1.5 rounded-md hover:bg-clay-muted transition-colors" title="Delete" aria-label={`Delete "${n.title}"`}>
                    <Trash2 className="w-3.5 h-3.5 text-faint" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
